import {
  useListProvidersBySite,
  useListPayors,
  useListStatus,
  useListTariffs,
  useListSites,
} from 'api/resource';
import { useListStatements } from 'api/resource/statements';
import { ProviderSelect } from 'components/ProviderSelect';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useIsMobile } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { StatementManagement } from '..';
import { Box, Group } from '@mantine/core';

export const AdminStatementManagement: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const { id } = useCurrentSiteStore();
  const [selectedProviderID, setSelectedProviderID] = useState<string | null>(
    null
  );

  const { data: payors = [] } = useListPayors();

  const { data: statuses = [] } = useListStatus({});

  const { data: tariffs = [] } = useListTariffs({});

  const { data: sites = [] } = useListSites({
    isAdmin,
  });

  const {
    data: statements = [],
    isLoading: loadingStatements,
    isError: isStatementsError,
    error: statementsError,
  } = useListStatements({
    isAdmin,
    siteID: id,
  });

  const {
    data: providers = [],
    isLoading: loadingProviders,
    isError: errorProvider,
    error: providerError,
  } = useListProvidersBySite({ siteID: id });

  useEffect(() => {
    if (
      selectedProviderID &&
      !providers.some((provider) => provider.id === selectedProviderID)
    ) {
      setSelectedProviderID(null);
    }
  }, [providers, selectedProviderID]);

  const filteredStatements = useMemo(() => {
    if (!selectedProviderID) return statements;
    return statements.filter(
      (statement) => statement.providerID === selectedProviderID
    );
  }, [statements, selectedProviderID]);

  const isLoading = loadingProviders || loadingStatements;
  const isError = errorProvider || isStatementsError;
  const errorMessage = providerError?.message || statementsError?.message;

  const providerFilter = (
    <Group gap="sm" align="center" w="100%">
      <ProviderSelect
        providers={providers}
        value={selectedProviderID}
        onChange={(value) => setSelectedProviderID(value)}
        placeholder="All providers"
        label="Provider"
        clearable
        searchable
        withinPortal
        disabled={!providers.length || loadingProviders}
        allowDeselect
        w={280}
        dataTestId="statement-provider-select"
      />
    </Group>
  );

  return (
    <>
      {isMobile && <Box mb="sm">{providerFilter}</Box>}
      <StatementManagement
        statements={filteredStatements}
        payors={payors}
        statuses={statuses}
        providers={providers}
        tariffs={tariffs}
        isLoading={isLoading}
        isError={isError}
        errorMessage={errorMessage}
        showProviderColumn
        renderTopToolbarCustomActions={
          isMobile ? undefined : () => providerFilter
        }
        sites={sites}
      />
    </>
  );
};
